import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const SITE_INFO = {
  name: 'Scalemage',
  description: 'AI-powered image adaptation tool that transforms images between different advertising formats. Upload one image and instantly generate versions optimized for every major ad platform.',
  url: 'https://scalemage.co',
  pricing: {
    freeCredits: 3,
    paidCredits: { amount: 10, price: '$3 USD' },
    costPerAdaptation: '1 credit per format',
  },
  features: [
    'AI-powered intelligent recomposition (not just cropping)',
    'Multi-format generation from one upload',
    'Creative context support for guided adaptation',
    'High quality full-resolution outputs',
  ],
  supportedInputFormats: ['JPEG', 'PNG', 'WebP'],
};

const FORMATS = [
  { id: 'square', name: 'Square', dimensions: '1080×1080', aspectRatio: '1:1', useCases: 'Instagram Feed, Facebook Feed, LinkedIn' },
  { id: 'instagram', name: 'Instagram Portrait', dimensions: '1080×1350', aspectRatio: '4:5', useCases: 'Instagram Feed (portrait), Facebook Feed' },
  { id: 'portrait', name: 'Portrait / Stories', dimensions: '1080×1920', aspectRatio: '9:16', useCases: 'Instagram Stories, TikTok, Facebook Stories, Reels' },
  { id: 'horizontal', name: 'Horizontal / Landscape', dimensions: '1920×1080', aspectRatio: '16:9', useCases: 'YouTube thumbnails, Display ads, Twitter/X' },
];

const VALID_FORMATS = ['square', 'portrait', 'horizontal', 'instagram'];

function textResponse(text: string) {
  return { content: [{ type: 'text', text }] };
}

async function fetchImageAsBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

  const blob = await response.blob();
  if (blob.size > 10 * 1024 * 1024) throw new Error('Image exceeds 10MB size limit');

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function WebMCP() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('modelContext' in navigator) || !navigator.modelContext) return;

    navigator.modelContext.provideContext({
      tools: [
        {
          name: 'get-site-info',
          description: 'Get information about Scalemage: what it does, pricing, features, and supported formats.',
          inputSchema: { type: 'object', properties: {} },
          execute: async () => textResponse(JSON.stringify(SITE_INFO, null, 2)),
        },
        {
          name: 'get-available-formats',
          description: 'Get the list of image output formats Scalemage supports, including dimensions, aspect ratios, and use cases.',
          inputSchema: { type: 'object', properties: {} },
          execute: async () => textResponse(JSON.stringify(FORMATS, null, 2)),
        },
        {
          name: 'navigate-to-page',
          description: 'Navigate to a Scalemage page. Valid pages: home, studio, auth.',
          inputSchema: {
            type: 'object',
            properties: {
              page: { type: 'string', enum: ['home', 'studio', 'auth'], description: 'Page to navigate to' },
            },
            required: ['page'],
          },
          execute: async (args) => {
            const routes: Record<string, string> = { home: '/', studio: '/studio', auth: '/auth' };
            const page = args.page as string;
            const route = routes[page];
            if (!route) return textResponse(`Invalid page "${page}". Valid: home, studio, auth.`);
            window.location.href = route;
            return textResponse(`Navigating to ${page} (${route})`);
          },
        },
        {
          name: 'adapt-image',
          description:
            'Adapt an image to a target advertising format using AI. Costs 1 credit per call. The user must be logged in and have credits. Returns the adapted image as a base64 data URL.',
          inputSchema: {
            type: 'object',
            properties: {
              imageUrl: { type: 'string', description: 'Public URL of the source image to adapt' },
              format: {
                type: 'string',
                enum: VALID_FORMATS,
                description: 'Target format: square (1080×1080), instagram (1080×1350), portrait (1080×1920), or horizontal (1920×1080)',
              },
              context: { type: 'string', description: 'Optional creative direction to guide the AI adaptation' },
            },
            required: ['imageUrl', 'format'],
          },
          execute: async (args) => {
            const { imageUrl, format, context } = args as { imageUrl: string; format: string; context?: string };

            if (!VALID_FORMATS.includes(format)) {
              return textResponse(`Invalid format "${format}". Valid: ${VALID_FORMATS.join(', ')}`);
            }

            // Check auth
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
              return textResponse('User is not logged in. Please sign in at /auth first to use image adaptation.');
            }

            // Fetch and convert image
            let base64Data: string;
            try {
              base64Data = await fetchImageAsBase64(imageUrl);
            } catch (err) {
              return textResponse(`Failed to fetch image: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }

            // Call edge function
            const { data, error } = await supabase.functions.invoke('adapt-image', {
              body: { image: base64Data, format, context: context || '' },
            });

            if (error) {
              return textResponse(`Adaptation failed: ${error.message}`);
            }

            if (data?.error) {
              return textResponse(`Adaptation failed: ${data.error}`);
            }

            return textResponse(JSON.stringify({
              success: true,
              format,
              adaptedImage: data.image,
              message: `Image adapted to ${format} format. 1 credit used.`,
            }));
          },
        },
      ],
    });
  }, []);

  return null;
}
